<script setup lang="ts">
// Tukey-style box plot — `boxplot` type discriminant. Functionally
// identical to the legacy `box` chart; this component is the renamed
// landing site that the v1 migration message points authors at.
//
// Two input modes (mutually exclusive, enforced at the type level):
//
//   1. Raw — `spec.value` names a numeric column. The host groups by
//      `spec.category`, computes the five summary stats per group with
//      linear-interpolation quartiles, and identifies outliers via the
//      1.5×IQR rule.
//   2. Pre-computed — `spec.quartiles` names the five summary columns,
//      one box per row. `spec.outliers` is an optional column carrying
//      an array of outlier values.
//
// Click datums carry the category and the box's five stats; outlier
// dots tag the datum with `_kind: "outlier"`.

import { computed } from "vue";

import { resolveChartHeight } from "./chart-heights";
import VChart from "vue-echarts";
import "../echarts.ts";

import type { BoxplotChartSpec } from "../sbi/protocol";
import { COLORS } from "../theme.ts";
import { baseOption, chartTextColor, tooltipDefaults } from "./echarts-base";
import { categoryLabelPolicy, plotWidthEstimate, verticalCategoryLabel } from "./chart-layout";
import { useChartLayout } from "./use-chart-layout";

// The chart's own width / print / touch (see chart-layout.ts).
const { chartRef, ctx } = useChartLayout();

const props = withDefaults(
  defineProps<{
    spec: BoxplotChartSpec;
    formats?: Record<string, string>;
  }>(),
  { formats: () => ({}) },
);

const emit = defineEmits<{
  (e: "click-target", id: string, datum: Record<string, unknown>): void;
}>();

interface BoxRow {
  category: string | number;
  min: number;
  q1: number;
  median: number;
  q3: number;
  max: number;
  outliers: ReadonlyArray<number>;
  n?: number;
}

function quantile(sorted: ReadonlyArray<number>, p: number): number {
  const n = sorted.length;
  if (n === 0) return NaN;
  if (n === 1) return sorted[0]!;
  const h = (n - 1) * p;
  const lo = Math.floor(h);
  const hi = Math.ceil(h);
  if (lo === hi) return sorted[lo]!;
  return sorted[lo]! + (sorted[hi]! - sorted[lo]!) * (h - lo);
}

function computeFromRaw(spec: BoxplotChartSpec): BoxRow[] {
  if (!spec.value) return [];
  const rows = spec.data as ReadonlyArray<Record<string, unknown>>;
  const groups = new Map<string | number, number[]>();
  for (const row of rows) {
    const cat = row[spec.category] as string | number;
    const raw = row[spec.value];
    const v = typeof raw === "number" ? raw : Number(raw);
    if (!Number.isFinite(v)) continue;
    let bucket = groups.get(cat);
    if (!bucket) {
      bucket = [];
      groups.set(cat, bucket);
    }
    bucket.push(v);
  }
  const out: BoxRow[] = [];
  for (const [cat, values] of groups) {
    if (values.length === 0) continue;
    values.sort((a, b) => a - b);
    const q1 = quantile(values, 0.25);
    const median = quantile(values, 0.5);
    const q3 = quantile(values, 0.75);
    const iqr = q3 - q1;
    const lowerFence = q1 - 1.5 * iqr;
    const upperFence = q3 + 1.5 * iqr;
    const outliers: number[] = [];
    let whiskerLo = Infinity;
    let whiskerHi = -Infinity;
    for (const v of values) {
      if (v < lowerFence || v > upperFence) {
        outliers.push(v);
      } else {
        if (v < whiskerLo) whiskerLo = v;
        if (v > whiskerHi) whiskerHi = v;
      }
    }
    if (!Number.isFinite(whiskerLo)) {
      whiskerLo = values[0]!;
      whiskerHi = values[values.length - 1]!;
    }
    out.push({
      category: cat,
      min: whiskerLo,
      q1,
      median,
      q3,
      max: whiskerHi,
      outliers,
      n: values.length,
    });
  }
  return out;
}

function computeFromPrecomputed(spec: BoxplotChartSpec): BoxRow[] {
  if (!spec.quartiles) return [];
  const q = spec.quartiles;
  const rows = spec.data as ReadonlyArray<Record<string, unknown>>;
  const out: BoxRow[] = [];
  for (const row of rows) {
    const cat = row[spec.category] as string | number;
    const min = Number(row[q.min]);
    const q1 = Number(row[q.q1]);
    const median = Number(row[q.median]);
    const q3 = Number(row[q.q3]);
    const max = Number(row[q.max]);
    if (![min, q1, median, q3, max].every(Number.isFinite)) continue;
    let outliers: number[] = [];
    if (spec.outliers) {
      const raw = row[spec.outliers];
      if (Array.isArray(raw)) {
        outliers = raw
          .map((v) => Number(v))
          .filter((v) => Number.isFinite(v));
      }
    }
    out.push({ category: cat, min, q1, median, q3, max, outliers });
  }
  return out;
}

const boxes = computed<BoxRow[]>(() =>
  props.spec.quartiles
    ? computeFromPrecomputed(props.spec)
    : computeFromRaw(props.spec),
);

const option = computed(() => {
  const valueFormat =
    props.spec.valueFormat ??
    props.spec.format ??
    (props.spec.value ? props.formats[props.spec.value] : undefined);
  const base = baseOption({ title: props.spec.title, yFormat: valueFormat }, ctx.value);
  const categories = boxes.value.map((b) => b.category);
  const categoryAxis = {
    ...base.xAxis,
    type: "category" as const,
    data: categories,
    boundaryGap: true,
    // Horizontal: names sit in the left gutter — cap and truncate them
    // like horizontal bars. Vertical: rotate / truncate to fit slots.
    axisLabel: {
      ...base.xAxis.axisLabel,
      ...(props.spec.horizontal
        ? verticalCategoryLabel(categories, ctx.value.width)
        : categoryLabelPolicy({
            labels: categories,
            plotWidth: plotWidthEstimate(ctx.value.width),
            mode: "rotate",
          })),
    },
  };
  const boxData = boxes.value.map((b) => [b.min, b.q1, b.median, b.q3, b.max]);
  const outlierData: [number, number][] = [];
  boxes.value.forEach((b, i) => {
    for (const o of b.outliers) outlierData.push([i, o]);
  });
  const valueAxis = { ...base.yAxis, type: "value" as const };
  return {
    ...base,
    tooltip: tooltipDefaults("item", ctx.value),
    xAxis: props.spec.horizontal ? valueAxis : categoryAxis,
    yAxis: props.spec.horizontal ? categoryAxis : valueAxis,
    series: [
      {
        type: "boxplot" as const,
        data: boxData,
        itemStyle: { color: COLORS.primary, borderColor: chartTextColor() },
      },
      {
        type: "scatter" as const,
        data: outlierData,
        symbolSize: 5,
        itemStyle: { color: COLORS.negative },
        z: 2,
        name: "_outliers",
      },
    ],
    legend: { show: false },
  };
});

const height = computed(() =>
  resolveChartHeight("boxplot", props.spec.height),
);

function onClick(p: {
  seriesIndex?: number;
  dataIndex?: number;
  data?: unknown;
}) {
  if (!props.spec.id || !p) return;
  if (typeof p.dataIndex !== "number") return;
  if (p.seriesIndex === 1) {
    const point = p.data as [number, number] | undefined;
    if (!point) return;
    const [catIndex, value] = point;
    const box = boxes.value[catIndex];
    if (!box) return;
    emit("click-target", props.spec.id, {
      _kind: "outlier",
      category: box.category,
      value,
    });
    return;
  }
  const box = boxes.value[p.dataIndex];
  if (!box) return;
  emit("click-target", props.spec.id, {
    category: box.category,
    min: box.min,
    q1: box.q1,
    median: box.median,
    q3: box.q3,
    max: box.max,
    ...(box.n !== undefined ? { n: box.n } : {}),
  });
}
</script>

<template>
  <v-chart
    ref="chartRef"
    class="chart"
    :style="{ height: `${height}px` }"
    :option="option"
    :init-options="{ renderer: 'canvas' }"
    autoresize
    @click="onClick"
  />
</template>

<style scoped>
.chart {
  width: 100%;
}
</style>
